import { useMemo } from "react";
import { ChevronLeft, LayoutDashboard, Search } from "lucide-react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  STAMMDATEN_NAV_GROUPS,
  getStammdatenGroupForItem,
  getStammdatenNavByKey,
  getStammdatenNavByPath,
  type NavItem,
} from "@/lib/nav";
import { classNames } from "@/lib/format";
import { NavBadge } from "@/navigation/NavBadge";
import { NavIcon } from "@/navigation/NavIcon";
import { useNav } from "@/navigation/navContext";
import { StammdatenPageHeader } from "@/pages/stammdaten/StammdatenPageHeader";

function isStammdatenOverview(pathname: string): boolean {
  const normalized = pathname.replace(/\/+$/, "") || "/";
  return normalized === "/stammdaten";
}

function StammdatenSidebarLink({ item }: { item: NavItem }) {
  const { pathname } = useLocation();
  const { badges } = useNav();
  const base = item.href.split("?")[0] ?? item.href;
  const active = pathname === base || pathname.startsWith(`${base}/`);

  return (
    <NavLink
      to={item.href}
      title={item.description}
      className={classNames(
        "flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors",
        active
          ? "bg-accent text-label-on-accent shadow-sm"
          : "text-label-secondary hover:bg-fill hover:text-label",
      )}
    >
      {item.icon ? <NavIcon name={item.icon} size={16} /> : null}
      <span className="min-w-0 flex-1 leading-snug">{item.label}</span>
      <NavBadge item={item} badges={badges} active={active} />
    </NavLink>
  );
}

export function StammdatenLayout() {
  const { pathname } = useLocation();
  const isOverview = isStammdatenOverview(pathname);
  const currentItem = isOverview ? undefined : getStammdatenNavByPath(pathname);
  const currentGroup = currentItem ? getStammdatenGroupForItem(currentItem.key) : undefined;

  const groups = useMemo(
    () =>
      STAMMDATEN_NAV_GROUPS.map((group) => ({
        ...group,
        items: group.itemKeys
          .map((key) => getStammdatenNavByKey(key))
          .filter((item): item is NavItem => item != null),
      })).filter((group) => group.items.length > 0),
    [],
  );

  return (
    <div className="flex min-w-0 flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
      <nav
        aria-label="Stammdaten-Bereiche"
        className="hidden lg:sticky lg:top-[calc(var(--eh-app-header-height)+1.25rem)] lg:block lg:w-60 lg:shrink-0"
      >
        <div className="flex flex-col gap-1 rounded-xl border border-separator bg-grouped p-2">
          <NavLink
            to="/stammdaten"
            end
            className={({ isActive }) =>
              classNames(
                "flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm font-semibold transition-colors",
                isActive
                  ? "bg-accent text-label-on-accent shadow-sm"
                  : "text-label hover:bg-fill",
              )
            }
          >
            <LayoutDashboard size={16} strokeWidth={1.75} aria-hidden className="shrink-0" />
            <span className="min-w-0 flex-1 truncate">Übersicht</span>
            <Search size={14} aria-hidden className="shrink-0 opacity-60" />
          </NavLink>

          {groups.map((group) => (
            <section key={group.key} className="mt-2 first:mt-0">
              <h2
                className="mb-1 px-2.5 text-[0.625rem] font-semibold uppercase tracking-wide text-label-tertiary"
                title={group.description}
              >
                {group.label}
              </h2>
              <div className="flex flex-col gap-0.5">
                {group.items.map((item) => (
                  <StammdatenSidebarLink key={item.key} item={item} />
                ))}
              </div>
            </section>
          ))}
        </div>
      </nav>

      <div className="min-w-0 flex-1 space-y-6">
        {!isOverview ? (
          <NavLink
            to="/stammdaten"
            className="inline-flex items-center gap-1 text-sm font-medium text-accent lg:hidden"
          >
            <ChevronLeft size={18} strokeWidth={2} aria-hidden />
            Alle Stammdaten
          </NavLink>
        ) : null}

        {currentItem ? <StammdatenPageHeader item={currentItem} group={currentGroup} /> : null}

        <Outlet />
      </div>
    </div>
  );
}
